import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Users, Shield, Crosshair, ArrowLeft, Hash, Copy, Crown, Zap, LogOut, Bot, Trophy, Search, X, Check, SkipForward } from "lucide-react";
import { apiClient } from "../lib/api";
import { useAuth } from "../lib/auth";
import ModeGate from "../components/ModeGate";
import MathText from "../components/MathText";
import ProtectedQuestion from "../components/ProtectedQuestion";
import MathKeyboard from "../components/MathKeyboard";
import MatchTools from "../components/MatchTools";
import IncorrectFlash from "../components/IncorrectFlash";
import LeaveConfirmModal from "../components/LeaveConfirmModal";
import useLeaveGuard from "../hooks/useLeaveGuard";
import useAntiCheat from "../hooks/useAntiCheat";
import useSubjectTopics from "../hooks/useSubjectTopics";

const SUBJECTS = ["Math","Physics","Chemistry","Biology","English","History","Geography"];
const GRADES   = ["Grade 9","Grade 10","Grade 11","Grade 12"];
const PURPLE   = "rgba(168,85,247,0.55)";

/* ── Entry: Multiplayer = TWO simple buttons ────────────────────────────── */
export default function Multiplayer() {
  return (
    <ModeGate mode="Multiplayer" accent="purple">
      <MultiplayerEntry />
    </ModeGate>
  );
}

function MultiplayerEntry() {
  const nav = useNavigate();
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <button onClick={()=>nav("/")} className="font-mono text-xs tracking-[0.3em] text-soft hover:text-white flex items-center gap-2 mb-6" data-testid="back-home"><ArrowLeft size={14}/>BACK TO MENU</button>
      <div className="font-mono text-[10px] tracking-[0.4em] text-purple uppercase mb-2">MULTIPLAYER</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl mb-10">4V4 <span className="text-purple">TEAM BATTLES</span></h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <EntryCard
          icon={<Crosshair size={28}/>}
          title="QUICK MATCH"
          subtitle="Auto-team & instant 4v4. Pick subject, topic, grade — we handle the rest."
          onClick={()=>nav("/multiplayer/quick")}
          testid="mp-quick-match-btn"
        />
        <EntryCard
          icon={<Users size={28}/>}
          title="CREATE TEAM LOBBY"
          subtitle="Get a code, invite friends, queue together. Team size is always 4."
          onClick={()=>nav("/multiplayer/lobby/create")}
          testid="mp-create-lobby-btn"
        />
      </div>

      <div className="mt-6 sm-card p-5 flex items-center gap-3">
        <Hash size={18} className="text-purple"/>
        <div className="flex-1">
          <div className="font-mono text-[10px] tracking-[0.3em] text-soft">HAVE A LOBBY CODE?</div>
          <div className="font-heading text-lg">Join your friend's team</div>
        </div>
        <button className="sm-btn-outline px-5 py-2.5 text-xs" onClick={()=>nav("/multiplayer/lobby/join")} data-testid="mp-join-lobby-btn">JOIN LOBBY</button>
      </div>
    </div>
  );
}

function EntryCard({ icon, title, subtitle, onClick, testid }) {
  return (
    <motion.button
      whileHover={{ y: -3 }}
      className="sm-card p-7 text-left h-full"
      onClick={onClick} data-testid={testid}
      style={{ borderColor: PURPLE, boxShadow: "0 0 28px rgba(168,85,247,0.20)" }}
    >
      <div className="w-12 h-12 grid place-items-center rounded-xl mb-4 border" style={{ borderColor: PURPLE, color: "var(--sm-purple-2)" }}>{icon}</div>
      <div className="font-heading font-black text-2xl tracking-wide">{title}</div>
      <div className="text-soft text-sm mt-2">{subtitle}</div>
    </motion.button>
  );
}

/* ── QUICK MATCH ─────────────────────────────────────────────────────────── */
export function MultiplayerQuick() {
  return (
    <ModeGate mode="Multiplayer · Quick Match" accent="purple">
      <QuickMatchFlow />
    </ModeGate>
  );
}

function QuickMatchFlow() {
  const nav = useNavigate();
  const [subject, setSubject] = useState("Math");
  const [grade, setGrade] = useState("Grade 10");
  const { topics, topic, setTopic } = useSubjectTopics(subject, grade, { extra: ["Mixed"] });
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(null); // {code, matchId}
  const [queueInfo, setQueueInfo] = useState({ elapsed: 0, waitingForReal: true, botGrace: 25, botForce: 60 });

  // Polling for an opponent once we're in queue
  useEffect(() => {
    if (!searching) return;
    const t = setInterval(async () => {
      try {
        const r = await apiClient.get(`/team/${searching.code}/state`);
        const lobby = r.data?.lobby;
        if (lobby) {
          setQueueInfo({
            elapsed: lobby.queued_elapsed_s ?? 0,
            waitingForReal: lobby.waiting_for_real ?? true,
            botGrace: lobby.bot_grace_s ?? 25,
            botForce: lobby.bot_force_s ?? 60,
          });
        }
        if (lobby?.team_match_id) {
          clearInterval(t);
          nav(`/multiplayer/match/${lobby.team_match_id}`);
        }
      } catch (e) { /* keep polling */ }
    }, 1500);
    return () => clearInterval(t);
  }, [searching, nav]);

  const findMatch = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post("/team/quick-match", { name: "Quick Squad", subject, topic: topic === "Mixed" ? null : topic, grade });
      setSearching({ code: r.data.code, matchId: r.data.match_id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quick match failed");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!searching) return;
    try { await apiClient.post(`/team/${searching.code}/leave`); } catch { /* ignore */ }
    setSearching(null);
    toast.info("Left matchmaking");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <button onClick={()=>nav("/multiplayer")} className="font-mono text-xs tracking-[0.3em] text-soft hover:text-white flex items-center gap-2 mb-6"><ArrowLeft size={14}/>BACK</button>
      <div className="font-mono text-[10px] tracking-[0.4em] text-purple uppercase mb-2">QUICK MATCH</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl mb-8">FIND A <span className="text-purple">4V4 MATCH</span></h1>

      {searching ? (
        <SearchingPanel onCancel={cancel} info={queueInfo}/>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <SelectorPurple label="SUBJECT" value={subject} options={SUBJECTS} onChange={setSubject} testid="quick-subject"/>
          <SelectorPurple label="TOPIC"   value={topic}   options={topics}   onChange={setTopic}   testid="quick-topic"/>
          <SelectorPurple label="GRADE"   value={grade}   options={GRADES}   onChange={setGrade}   testid="quick-grade"/>
        </div>
      )}

      {!searching && (
        <button onClick={findMatch} disabled={busy} className="sm-btn-primary px-8 py-4 w-full md:w-auto" data-testid="quick-find-btn">
          <Crosshair className="inline mr-2" size={16}/>FIND MATCH
        </button>
      )}

      <div className="mt-8 sm-card p-5">
        <div className="font-mono text-[10px] tracking-[0.3em] text-faint mb-2">HOW IT WORKS</div>
        <ul className="text-sm text-soft space-y-1">
          <li>· You're auto-placed in a random team of 4.</li>
          <li>· We prioritize <span className="text-purple">real players</span> — bots only step in as a last-resort fallback after the wait window.</li>
          <li>· Matched against another random team of 4 with the same subject + grade.</li>
          <li>· Each slot fights the matching slot on the enemy team (Slot 1 vs Slot 1, etc.).</li>
        </ul>
      </div>
    </div>
  );
}

function SearchingPanel({ onCancel, info }) {
  const elapsed = info?.elapsed ?? 0;
  const botGrace = info?.botGrace ?? 25;
  const waitingForReal = !!info?.waitingForReal && elapsed < botGrace;
  const pct = Math.min(100, (elapsed / Math.max(botGrace, 1)) * 100);
  const remaining = Math.max(0, botGrace - elapsed);
  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} className="sm-card p-8 mb-6 text-center" style={{ borderColor: PURPLE }} data-testid="quick-search-panel">
      <div className="mx-auto w-16 h-16 grid place-items-center rounded-full border border-purple-500/50">
        <Search size={28} className="text-purple animate-pulse"/>
      </div>
      <div className="mt-4 font-heading font-bold text-xl">
        {waitingForReal ? "WAITING FOR REAL PLAYERS…" : "EXPANDING SEARCH — BOTS MAY FILL IN…"}
      </div>
      <div className="text-soft text-sm mt-2">
        {waitingForReal
          ? <>Quick Match prioritizes real opponents. We'll fill any empty slots with bots in <span className="text-purple font-mono">{remaining}s</span> if no one shows.</>
          : <>No real opponents found in the grace window — completing your match with bots so you don't have to wait.</>}
      </div>
      <div className="mt-5 max-w-md mx-auto h-1.5 bg-white/8 rounded-full overflow-hidden">
        <motion.div className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500"
          initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }}/>
      </div>
      <div className="mt-2 font-mono text-[10px] tracking-[0.3em] text-faint">QUEUE TIME · {elapsed}s</div>
      <button onClick={onCancel} className="sm-btn-outline mt-6 px-5 py-2.5 text-xs" data-testid="quick-cancel-btn">
        <X size={14} className="inline mr-1"/> CANCEL
      </button>
    </motion.div>
  );
}

/* ── CREATE TEAM LOBBY ────────────────────────────────────────────────────── */
export function MultiplayerLobbyCreate() {
  return (
    <ModeGate mode="Multiplayer · Lobby" accent="purple">
      <CreateLobbyForm />
    </ModeGate>
  );
}

function CreateLobbyForm() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("Math");
  const [grade, setGrade] = useState("Grade 10");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim()) { toast.error("Team name required"); return; }
    setBusy(true);
    try {
      const r = await apiClient.post("/team/create", { name: name.trim(), subject, grade });
      nav(`/multiplayer/lobby/${r.data.code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create lobby");
    } finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <button onClick={()=>nav("/multiplayer")} className="font-mono text-xs tracking-[0.3em] text-soft hover:text-white flex items-center gap-2 mb-6"><ArrowLeft size={14}/>BACK</button>
      <div className="font-mono text-[10px] tracking-[0.4em] text-purple uppercase mb-2">CREATE TEAM LOBBY</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl mb-8">YOUR <span className="text-purple">SQUAD</span>, YOUR RULES</h1>

      <div className="sm-card p-6 space-y-5">
        <label className="block">
          <div className="font-mono text-[10px] tracking-[0.3em] text-purple">TEAM NAME</div>
          <input className="sm-input mt-2" value={name} onChange={(e)=>setName(e.target.value)} placeholder="e.g. Study Kings" maxLength={30} data-testid="lobby-name-input"/>
        </label>
        <SelectorPurple label="SUBJECT" value={subject} options={SUBJECTS} onChange={setSubject} testid="lobby-subject"/>
        <SelectorPurple label="GRADE"   value={grade}   options={GRADES}   onChange={setGrade}   testid="lobby-grade"/>

        <div className="text-xs text-faint font-mono tracking-widest pt-1">TEAM SIZE: 4 (FIXED)</div>

        <button onClick={create} disabled={busy} className="sm-btn-primary w-full py-3.5" data-testid="lobby-create-submit">
          {busy ? "Creating…" : "CREATE LOBBY"}
        </button>
      </div>
    </div>
  );
}

/* ── JOIN LOBBY ───────────────────────────────────────────────────────────── */
export function MultiplayerLobbyJoin() {
  return (
    <ModeGate mode="Multiplayer · Join" accent="purple">
      <JoinLobbyForm />
    </ModeGate>
  );
}

function JoinLobbyForm() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const join = async () => {
    const c = code.trim().toUpperCase();
    if (c.length < 4) { toast.error("Enter a valid code"); return; }
    setBusy(true);
    try {
      await apiClient.post(`/team/join/${c}`);
      nav(`/multiplayer/lobby/${c}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join");
    } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <button onClick={()=>nav("/multiplayer")} className="font-mono text-xs tracking-[0.3em] text-soft hover:text-white flex items-center gap-2 mb-6"><ArrowLeft size={14}/>BACK</button>
      <h1 className="font-heading font-black text-3xl mb-8">JOIN <span className="text-purple">LOBBY</span></h1>
      <div className="sm-card p-6 space-y-5">
        <label className="block">
          <div className="font-mono text-[10px] tracking-[0.3em] text-purple">LOBBY CODE</div>
          <input className="sm-input mt-2 font-mono tracking-[0.4em] uppercase text-center text-xl" value={code} onChange={(e)=>setCode(e.target.value.toUpperCase())} maxLength={6} placeholder="XXXXXX" data-testid="lobby-code-input"/>
        </label>
        <button onClick={join} disabled={busy} className="sm-btn-primary w-full py-3.5" data-testid="lobby-join-submit">
          {busy ? "Joining…" : "JOIN TEAM"}
        </button>
      </div>
    </div>
  );
}

/* ── LOBBY ROOM ──────────────────────────────────────────────────────────── */
export function MultiplayerLobby() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [lobby, setLobby] = useState(null);
  const [busy, setBusy] = useState(false);

  // Poll for lobby state + auto-redirect to match when matched
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await apiClient.get(`/team/${code}/state`);
        if (!alive) return;
        setLobby(r.data.lobby);
        if (r.data.lobby?.team_match_id) {
          nav(`/multiplayer/match/${r.data.lobby.team_match_id}`);
        }
      } catch (e) { /* ignore transient errors */ }
    };
    tick();
    const t = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(t); };
  }, [code, nav]);

  const findMatch = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/team/${code}/find-match`);
      toast.success("Searching for opponent...");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Find match failed");
    } finally { setBusy(false); }
  };

  const leave = async () => {
    try { await apiClient.post(`/team/${code}/leave`); } catch {}
    nav("/multiplayer");
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied");
  };

  if (!lobby) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <div className="font-mono text-xs tracking-widest text-faint">LOADING LOBBY…</div>
      </div>
    );
  }

  const isHost = lobby.host_id === user?.id;
  const isSearching = lobby.status === "searching";
  const slots = [0,1,2,3].map((i) => lobby.players.find((p) => p.slot === i) || null);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <button onClick={leave} className="font-mono text-xs tracking-[0.3em] text-soft hover:text-white flex items-center gap-2 mb-6" data-testid="lobby-leave"><LogOut size={14}/>LEAVE LOBBY</button>

      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.4em] text-purple uppercase">TEAM LOBBY</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">{lobby.name}</h1>
          <div className="text-soft text-sm mt-1">
            <span className="text-purple">{lobby.subject}</span> · <span>{lobby.grade}</span>
          </div>
        </div>
        <button onClick={copyCode} className="sm-card px-4 py-3 flex items-center gap-3 hover:border-purple-500/60" data-testid="lobby-code-copy">
          <Hash size={16} className="text-purple"/>
          <div className="font-mono font-bold tracking-[0.3em] text-lg">{lobby.code}</div>
          <Copy size={14} className="text-faint"/>
        </button>
      </div>

      <div className="sm-card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-xs tracking-[0.3em] text-purple">SQUAD ({lobby.players.length}/4)</div>
          <div className="font-mono text-[10px] tracking-widest text-faint">EMPTY SLOTS WILL BE FILLED BY BOTS</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {slots.map((p, i) => <SlotRow key={i} index={i} p={p} hostId={lobby.host_id} meId={user?.id}/>)}
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        {isHost && !isSearching && (
          <button onClick={findMatch} disabled={busy} className="sm-btn-primary px-7 py-3" data-testid="lobby-find-match">
            <Crosshair className="inline mr-2" size={14}/>FIND MATCH
          </button>
        )}
        {isSearching && (
          <div className="sm-card px-5 py-3 flex items-center gap-3" style={{ borderColor: PURPLE }}>
            <Search size={18} className="text-purple animate-pulse"/>
            <div>
              <div className="font-heading text-sm">Searching for an opponent…</div>
              <div className="font-mono text-[10px] tracking-widest text-faint">YOU'LL JOIN AS SOON AS A 4V4 IS READY</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SlotRow({ index, p, hostId, meId }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-white/8 bg-white/[0.02]">
      <div className="w-9 h-9 grid place-items-center rounded-lg bg-purple-500/10 border border-purple-500/40 font-mono text-xs">
        {index+1}
      </div>
      <div className="flex-1">
        <div className="font-heading font-bold">
          {p ? p.username : <span className="text-faint italic font-mono text-xs">EMPTY · BOT</span>}
          {p && p.id === hostId && <Crown size={12} className="inline text-amber ml-2"/>}
          {p && p.id === meId && <span className="ml-2 text-[10px] font-mono tracking-widest text-purple">YOU</span>}
        </div>
        {p && <div className="text-[10px] font-mono tracking-widest text-faint">ELO {p.elo}</div>}
      </div>
      {p ? <Check size={14} className="text-win"/> : <Bot size={14} className="text-faint"/>}
    </div>
  );
}

/* ── MATCH SCREEN (4v4 polling) ──────────────────────────────────────────── */
export function MultiplayerMatch() {
  const { matchId } = useParams();
  return (
    <ModeGate mode="Multiplayer Match" accent="purple">
      <TeamMatchRoom matchId={matchId}/>
    </ModeGate>
  );
}

function TeamMatchRoom({ matchId }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [match, setMatch] = useState(null);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [wrongTrigger, setWrongTrigger] = useState(0);
  const [skipping, setSkipping] = useState(false);
  const [now, setNow] = useState(Date.now());
  const startTsRef = useRef(0);
  const submittedRoundRef = useRef(-1);

  // 4v4 leave rule: tell the server, but the match keeps running for teammates.
  // Only when EVERY real player on one team has left does the backend end it.
  const sendTeamLeave = async () => {
    if (leaving || !match || match.status !== "active") return;
    setLeaving(true);
    try { await apiClient.post(`/team-match/${matchId}/leave`); }
    catch (e) { /* best-effort */ }
  };

  // Leave guard — active while the match is in progress (not finished, and we're a participant)
  const leaveGuard = useLeaveGuard({
    enabled: !!match && match.status === "active",
    onConfirmLeave: sendTeamLeave,
  });

  // Polling cadence: 1.2s while active, slowed when finished
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await apiClient.get(`/team-match/${matchId}/state`);
        if (!alive) return;
        setMatch(r.data.match);
      } catch (e) { /* keep polling */ }
    };
    tick();
    const t = setInterval(tick, 1200);
    return () => { alive = false; clearInterval(t); };
  }, [matchId]);

  // Reset answer + start timestamp whenever the round advances
  useEffect(() => {
    if (!match) return;
    setAnswer(""); setFeedback(null);
    startTsRef.current = Date.now();
    submittedRoundRef.current = -1;
  }, [match?.current_round, matchId]);

  // 1s ticker so the round countdown updates between polls.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Anti-cheat state. We have to declare this BEFORE any early return so
  // hooks are called in the same order every render. The handler uses refs
  // for the latest match/answer state.
  const matchRef = useRef(match);
  matchRef.current = match;
  const userRef = useRef(user);
  userRef.current = user;
  const submitForfeit = async (reason) => {
    const m = matchRef.current;
    if (!m || m.status !== "active") return;
    const u = userRef.current;
    const inA = m.team_a.players.find((p) => p.id === u?.id);
    const inB = m.team_b.players.find((p) => p.id === u?.id);
    const side = inA ? "a" : inB ? "b" : null;
    const slot = (inA || inB)?.slot ?? null;
    if (side === null) return;
    const round = m.rounds[m.current_round] || m.rounds[m.rounds.length - 1];
    const mine = round && slot !== null ? round.answers?.[String(slot)]?.[side] : null;
    if (mine) return;
    if (submittedRoundRef.current === m.current_round) return;
    submittedRoundRef.current = m.current_round;
    try {
      await apiClient.post(`/team-match/${matchId}/answer`, {
        answer: "", elapsed_ms: Date.now() - startTsRef.current,
        forfeit: true, forfeit_reason: reason,
      });
      toast.error("Round forfeited — anti-cheat (tab switch detected).");
    } catch {}
  };
  const antiCheat = useAntiCheat({
    enabled: !!match && match.status === "active",
    roundKey: `${matchId}:${match?.current_round}`,
    onForfeit: submitForfeit,
  });

  if (!match) {
    return <div className="max-w-4xl mx-auto p-20 text-center font-mono text-xs tracking-widest text-faint">LOADING MATCH…</div>;
  }

  // Determine my side & slot
  const meInA = match.team_a.players.find((p) => p.id === user?.id);
  const meInB = match.team_b.players.find((p) => p.id === user?.id);
  const mySide = meInA ? "a" : meInB ? "b" : null;
  const mySlot = (meInA || meInB)?.slot ?? null;
  const isSpectator = mySide === null;

  const finished = match.status === "finished";
  const currentRound = match.rounds[match.current_round] || match.rounds[match.rounds.length-1];
  const myPair = mySlot !== null ? match.pairs[mySlot] : null;
  const myOpponent = myPair ? (mySide === "a" ? myPair.b : myPair.a) : null;
  const myAnswer = currentRound && mySlot !== null ? currentRound.answers?.[String(mySlot)]?.[mySide] : null;
  const oppAnswer = currentRound && mySlot !== null ? currentRound.answers?.[String(mySlot)]?.[mySide === "a" ? "b" : "a"] : null;
  // Each teammate has their OWN question for the round (slot-specific).
  const myQuestionText = currentRound && mySlot !== null ? currentRound.slots?.[String(mySlot)]?.question : null;
  const roundSecs = match.round_seconds || 120;
  const startedAtMs = currentRound?.started_at ? new Date(currentRound.started_at).getTime() : null;
  const timeLeft = startedAtMs ? Math.max(0, roundSecs - Math.floor((now - startedAtMs) / 1000)) : roundSecs;

  const submit = async (e) => {
    e?.preventDefault();
    if (!answer.trim() || finished || submitting || isSpectator) return;
    if (myAnswer) return;
    setSubmitting(true);
    try {
      const elapsed = Date.now() - startTsRef.current;
      const r = await apiClient.post(`/team-match/${matchId}/answer`, {
        answer, elapsed_ms: elapsed,
        suspicious: antiCheat.flagSuspiciousIfFast(answer, elapsed) || undefined,
      });
      setFeedback({ correct: r.data.correct });
      if (r.data && !r.data.correct) setWrongTrigger((n) => n + 1);
      submittedRoundRef.current = match.current_round;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  // Skip = forfeit this question (counts as unanswered/incorrect). 1.2s cooldown.
  const skip = async () => {
    if (skipping || finished || isSpectator || myAnswer) return;
    setSkipping(true);
    try {
      await apiClient.post(`/team-match/${matchId}/answer`, {
        answer: "", elapsed_ms: Date.now() - startTsRef.current, forfeit: true, forfeit_reason: "skip",
      });
      submittedRoundRef.current = match.current_round;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not skip");
    } finally { setTimeout(() => setSkipping(false), 1200); }
  };

  const teamAWins = match.pairs.filter(p => p.wins_a > p.wins_b).length;
  const teamBWins = match.pairs.filter(p => p.wins_b > p.wins_a).length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <IncorrectFlash trigger={wrongTrigger} />
      <button onClick={()=>leaveGuard.requestLeave("/")} className="font-mono text-xs tracking-[0.3em] text-soft hover:text-white flex items-center gap-2 mb-6" data-testid="mp-exit-match"><ArrowLeft size={14}/>EXIT MATCH</button>

      {/* Team Scoreboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <TeamHeader name={match.team_a.name} score={teamAWins} accent="blue" isWinner={finished && match.team_winner === "a"} side="a" mySide={mySide}/>
        <TeamHeader name={match.team_b.name} score={teamBWins} accent="purple" isWinner={finished && match.team_winner === "b"} side="b" mySide={mySide}/>
      </div>

      {/* Slot pairings: each row is "Player A · vs · Player B" */}
      <div className="sm-card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] tracking-[0.3em] text-soft">SLOT PAIRINGS · {match.subject}{match.topic?` · ${match.topic}`:""} · {match.grade}</div>
          <div className="flex items-center gap-3">
            <div className="font-mono text-xs text-purple">
              ROUND {Math.min(match.current_round+1, match.rounds.length)}/{match.rounds.length}
            </div>
            {!finished && (
              <div className={`font-heading font-black text-lg tabular-nums ${timeLeft <= 10 ? "text-loss animate-pulse" : "text-white"}`} data-testid="mp-timer">
                {timeLeft}s
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {match.pairs.map((p, i) => (
            <PairRow key={i} p={p} youSlot={mySlot} youSide={mySide}/>
          ))}
        </div>
      </div>

      {finished ? (
        <FinishedView match={match} mySide={mySide}/>
      ) : (
        <div className="sm-card p-7" style={{ borderColor: PURPLE }}>
          {isSpectator ? (
            <div className="text-center text-soft py-10">You're not a participant in this match.</div>
          ) : (
            <>
              <div className="font-mono text-[10px] tracking-[0.3em] text-purple mb-2">
                YOUR DUEL · SLOT {mySlot+1} · vs <span className="text-white">{myOpponent?.username}</span>
              </div>
              <div className="font-heading font-bold text-2xl sm:text-3xl">
                {myQuestionText
                  ? <ProtectedQuestion>{myQuestionText}</ProtectedQuestion>
                  : <span className="text-faint">Loading next question…</span>}
              </div>

              {antiCheat.warning && !myAnswer && (
                <div
                  data-testid="anticheat-warning"
                  className="mt-3 px-3 py-2 border border-loss/70 bg-loss/10 text-loss font-mono text-xs tracking-widest flex items-center justify-between"
                >
                  <span>⚠ {antiCheat.warning}</span>
                  <button
                    onClick={antiCheat.dismissWarning}
                    className="text-soft hover:text-white px-2"
                    data-testid="anticheat-dismiss"
                  >×</button>
                </div>
              )}

              {myAnswer ? (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <StatusCard
                    label="YOU"
                    correct={myAnswer.correct}
                    waiting={false}
                  />
                  <StatusCard
                    label={`OPPONENT · ${myOpponent?.username}`}
                    correct={oppAnswer?.correct}
                    waiting={!oppAnswer}
                  />
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div className="flex gap-3">
                    <input
                      autoFocus
                      className="sm-input flex-1"
                      value={answer}
                      onChange={(e)=>setAnswer(e.target.value)}
                      onPaste={(e)=>{ e.preventDefault(); toast.error("Pasting is disabled during matches."); }}
                      onDrop={(e)=>e.preventDefault()}
                      autoComplete="off"
                      spellCheck="false"
                      placeholder="Your answer..."
                      data-testid="mp-answer-input"
                    />
                    <button type="submit" className="sm-btn-primary px-6" disabled={submitting} data-testid="mp-submit-btn">
                      {submitting?"…":"SUBMIT"}
                    </button>
                  </div>
                  <MathKeyboard onInsert={(t)=>setAnswer((a)=>a+t)}/>
                  <div className="flex justify-end">
                    <button type="button" onClick={skip} disabled={skipping}
                      className="sm-btn-outline px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="mp-skip-btn">
                      <SkipForward size={12}/> SKIP QUESTION
                    </button>
                  </div>
                </form>
              )}

              {feedback && (
                <div className={`mt-4 text-sm font-mono tracking-widest ${feedback.correct?"text-win":"text-loss"}`}>
                  {feedback.correct ? "✓ CORRECT — WAITING FOR OPPONENT" : "✗ INCORRECT"}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Floating whiteboard + scientific calculator — only while the match
          is actively being played (not in lobby/queue or after it ends). */}
      {!isSpectator && match.status === "active" && (
        <MatchTools questionKey={match.current_round} />
      )}

      {/* "Are you sure?" — 4v4 rule: leaving does NOT instantly end the match */}
      <LeaveConfirmModal
        open={leaveGuard.open}
        onCancel={leaveGuard.cancel}
        onConfirm={leaveGuard.confirm}
        title="Are you sure you want to leave the game?"
        body="Your team will keep playing. The match only ends if everyone on your team leaves or disconnects."
      />
    </div>
  );
}

function TeamHeader({ name, score, accent, isWinner, side, mySide }) {
  const color = accent === "blue" ? "#3B82F6" : "#A855F7";
  return (
    <motion.div
      className="sm-card p-5 flex items-center gap-4"
      style={{ borderColor: color, boxShadow: isWinner ? `0 0 36px ${color}88` : undefined }}
      animate={isWinner ? { scale: [1, 1.03, 1] } : {}}
      transition={{ duration: 1.2, repeat: isWinner ? Infinity : 0 }}
    >
      <div className="w-12 h-12 grid place-items-center rounded-xl border" style={{ borderColor: color }}>
        {isWinner ? <Trophy size={22} style={{ color }}/> : <Shield size={22} style={{ color }}/>}
      </div>
      <div className="flex-1">
        <div className="font-mono text-[10px] tracking-[0.3em] text-soft">
          TEAM {side.toUpperCase()}{mySide === side && <span className="ml-2 text-[10px]" style={{ color }}>(YOU)</span>}
        </div>
        <div className="font-heading font-black text-xl truncate" style={{ color }}>{name}</div>
      </div>
      <div className="font-heading font-black text-4xl tabular-nums" style={{ color }}>{score}</div>
    </motion.div>
  );
}

function PairRow({ p, youSlot, youSide }) {
  const isYourPair = youSlot === p.slot;
  const aWon = p.wins_a > p.wins_b;
  const bWon = p.wins_b > p.wins_a;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${isYourPair ? "border-purple-500/50 bg-purple-500/[0.06]" : "border-white/8 bg-white/[0.02]"}`}>
      <div className="w-7 h-7 grid place-items-center rounded-md bg-white/5 border border-white/10 font-mono text-xs">{p.slot+1}</div>
      <div className={`flex-1 text-right font-heading ${aWon?"text-blue":"text-white/90"} ${youSide==="a" && isYourPair?"font-black":""}`}>
        {p.a.username}{p.a.is_bot && <Bot size={12} className="inline ml-1 text-faint"/>}
      </div>
      <div className="font-mono text-xs text-faint tabular-nums">
        <span className={aWon?"text-blue":""}>{p.wins_a}</span>
        <span className="text-faint mx-1">vs</span>
        <span className={bWon?"text-purple":""}>{p.wins_b}</span>
      </div>
      <div className={`flex-1 font-heading ${bWon?"text-purple":"text-white/90"} ${youSide==="b" && isYourPair?"font-black":""}`}>
        {p.b.username}{p.b.is_bot && <Bot size={12} className="inline ml-1 text-faint"/>}
      </div>
    </div>
  );
}

function StatusCard({ label, correct, waiting }) {
  return (
    <div className={`p-4 rounded-xl border-l-2 ${
      waiting ? "border-white/20 bg-white/[0.02]" :
      correct ? "border-[#34D399] bg-[#34D399]/[0.07]" :
      "border-[#F43F5E] bg-[#F43F5E]/[0.07]"
    }`}>
      <div className="font-mono text-[10px] tracking-[0.3em] text-soft">{label}</div>
      <div className={`font-heading font-bold text-lg mt-1 ${
        waiting ? "text-faint" : correct ? "text-win" : "text-loss"
      }`}>{waiting ? "WAITING…" : correct ? "CORRECT" : "INCORRECT"}</div>
    </div>
  );
}

function FinishedView({ match, mySide }) {
  const youWon = match.team_winner === mySide;
  const tie = match.team_winner === "draw";
  const forfeit = !!match.ended_by_forfeit;
  return (
    <motion.div
      initial={{opacity:0, scale:0.95}}
      animate={{opacity:1, scale:1}}
      className="sm-card p-10 text-center" style={{ borderColor: PURPLE, boxShadow: "0 0 40px rgba(168,85,247,0.3)" }}>
      <Trophy size={42} className="mx-auto text-amber"/>
      <div className="font-mono text-[10px] tracking-[0.4em] text-soft mt-3">
        {forfeit ? "MATCH ENDED · FORFEIT" : "MATCH COMPLETE"}
      </div>
      <div className="font-heading font-black text-4xl mt-3" data-testid="mp-end-title">
        {tie ? "DRAW" : youWon ? "YOUR TEAM WINS" : (mySide ? "DEFEAT" : "MATCH ENDED")}
      </div>
      {forfeit && youWon && (
        <div className="mt-3 text-sm text-soft" data-testid="mp-forfeit-note">
          The opposing team forfeited — every one of their players left or disconnected.
        </div>
      )}
      <div className="mt-3 text-soft text-sm">
        Final score: <span className="text-blue">{match.team_a.name} {match.pairs.filter(p=>p.wins_a>p.wins_b).length}</span> · <span className="text-purple">{match.pairs.filter(p=>p.wins_b>p.wins_a).length} {match.team_b.name}</span>
      </div>
    </motion.div>
  );
}

function SelectorPurple({ label, value, options, onChange, testid }) {
  return (
    <label className="sm-card p-4 block">
      <div className="font-mono text-[10px] tracking-[0.3em] text-purple">{label}</div>
      <select className="sm-input mt-2" value={value} onChange={(e)=>onChange(e.target.value)} data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
